import type { CalendarEvent } from '@serviceready/shared';
import type { ElementType } from '@serviceready/shared';
import type { Comment } from '@serviceready/shared';
import type {
  Template,
  OrphanTemplate,
  CreateTemplateInput,
  UpdateTemplateInput,
  PromoteSubmissionInput,
  PromoteOrphanInput,
} from '@serviceready/shared';
import type {
  Auxiliary,
  AuxiliaryMember,
  CreateAuxiliaryInput,
  UpdateAuxiliaryInput,
  AddAuxiliaryMemberInput,
  SetAuxiliaryLeaderInput,
} from '@serviceready/shared';
import type {
  Fund,
  BudgetLine,
  Transaction,
  CreateFundInput,
  CreateBudgetLineInput,
  CreateTransactionInput,
  TransactionDirection,
} from '@serviceready/shared';
import type {
  ReportTemplate,
  OrphanReportTemplate,
  ReportRun,
  ReportDefinition,
  InboxItem,
  ReportFormat,
  ReportRunStatus,
} from '@serviceready/shared';
import type {
  PromoteRunInput,
  PromoteReportOrphanInput,
  ShareReportRunInput,
} from '@serviceready/shared';

// Pre-Zod-parse caller shapes. The api-client accepts these (matching the optional
// fields on the TS interfaces in @serviceready/shared/types/report.ts); the server
// applies Zod defaults during validation.
type UpdateReportTemplateBody = {
  name?: string;
  description?: string | null;
  definition?: ReportDefinition;
};
type CreateReportRunBody =
  | { definition: ReportDefinition }
  | { templateId: string };

type Paginated<T> = {
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean };
};

const API_URL = import.meta.env.VITE_API_URL ?? '';

interface ApiError {
  error: string;
  message: string;
  details?: Record<string, string[]>;
}

interface ServiceDetail {
  id: string;
  churchId: string;
  name: string;
  serviceDate: string;
  serviceTime: string;
  status: string;
  publishedAt: string | null;
  description: string | null;
  submissions: Record<string, unknown>[];
  orderItems: OrderItemDetail[];
}

interface OrderItemDetail {
  id: string;
  serviceId: string;
  position: number;
  elementTypeId: string | null;
  title: string;
  notes: string | null;
  durationMinutes: number;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  elementTypeName: string | null;
  elementTypeCategory: string | null;
  elementTypeIcon: string | null;
  linkedSongAssignmentId: string | null;
}

interface SongDetail {
  id: string;
  title: string;
  artist: string | null;
  composer: string | null;
  ccliNumber: string | null;
  defaultKey: string | null;
  defaultTempo: number | null;
  timeSignature: string | null;
  tags: string[];
  arrangements: { id: string; name: string; key: string; tempo: number | null }[];
  resources: { id: string; fileName: string; fileUrl: string; resourceType: string }[];
}

interface MusicGroupDetail {
  id: string;
  name: string;
  groupType: string;
  memberCount: number;
}

interface ServiceSongAssignment {
  id: string;
  songId: string;
  purpose: string | null;
  performanceKey: string | null;
  position: number | null;
  songTitle: string;
  songArtist: string | null;
  songDefaultKey: string | null;
  arrangementName: string | null;
  arrangementKey: string | null;
  groupName: string | null;
  memberStatuses: { userId: string; userName: string; status: string }[];
}

interface MyAssignment {
  id: string;
  songTitle: string;
  songArtist: string | null;
  serviceName: string;
  serviceDate: string;
  arrangementName: string | null;
  arrangementKey: string | null;
  learningStatus: string;
  songDefaultKey: string | null;
}

interface CommentWithThread extends Comment {
  authorName: string;
  authorEmail: string;
  replies: (Comment & { authorName: string; authorEmail: string })[];
}

function getToken(): string | null {
  return localStorage.getItem('auth-token');
}

function setToken(token: string) {
  localStorage.setItem('auth-token', token);
}

function clearToken() {
  localStorage.removeItem('auth-token');
}

export class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = getToken();
    const headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
    };

    // Only declare a JSON content-type when we actually send a body. Fastify's JSON parser
    // rejects empty bodies as 400 when content-type is application/json, which breaks every
    // no-body mutation (resolve/unresolve/publish/claim/markRead/etc.).
    if (options.body !== undefined && options.body !== null) {
      headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Only auto-redirect for authenticated API calls (not login/register/me)
      const isAuthEndpoint = path.includes('/auth/login') || path.includes('/auth/register') || path.includes('/auth/me');
      if (!isAuthEndpoint) {
        clearToken();
        if (!window.location.pathname.includes('/sign-in')) {
          window.location.href = '/sign-in';
        }
      }
    }

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        error: 'Error',
        message: 'Something went wrong. Please try again.',
      }));
      throw error;
    }

    return response.json();
  }

  // Auth
  getMe() {
    return this.request<Record<string, unknown>>('/api/v1/auth/me');
  }

  /** Cross-app integration status for the caller's church. Read-only — link
   *  grants are a Cornerstone admin action, not something this app can perform. */
  getIntegrations() {
    return this.request<Record<string, unknown>>('/api/v1/integrations');
  }

  login(email: string, password: string) {
    return this.request<Record<string, unknown>>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  loginSelectChurch(email: string, password: string, userId: string) {
    return this.request<Record<string, unknown>>('/api/v1/auth/login/select-church', {
      method: 'POST',
      body: JSON.stringify({ email, password, userId }),
    });
  }

  register(data: {
    email: string;
    password: string;
    name: string;
    churchName: string;
    slug: string;
    denomination?: string;
    timezone: string;
    bibleTranslation: string;
  }) {
    return this.request<Record<string, unknown>>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Services
  getServices(params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return this.request<{ data: Record<string, unknown>[]; pagination: Record<string, unknown> }>(`/api/v1/services${query}`);
  }

  createService(data: Record<string, unknown>) {
    return this.request<Record<string, unknown>>('/api/v1/services', { method: 'POST', body: JSON.stringify(data) });
  }

  getService(id: string) {
    return this.request<ServiceDetail>(`/api/v1/services/${id}`);
  }

  getServiceReadiness(id: string) {
    return this.request<Record<string, unknown>>(`/api/v1/services/${id}/readiness`);
  }

  exportProclaim(id: string) {
    return this.request<Record<string, unknown>>(`/api/v1/services/${id}/export/proclaim`, { method: 'POST' });
  }

  // Submissions
  getSubmissions(params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return this.request<{ data: Record<string, unknown>[]; pagination: Record<string, unknown> }>(`/api/v1/submissions${query}`);
  }

  createSubmission(data: Record<string, unknown>) {
    return this.request<Record<string, unknown>>('/api/v1/submissions', { method: 'POST', body: JSON.stringify(data) });
  }

  getSubmission(id: string) {
    return this.request<Record<string, unknown>>(`/api/v1/submissions/${id}`);
  }

  updateSubmission(id: string, data: Record<string, unknown>) {
    return this.request<Record<string, unknown>>(`/api/v1/submissions/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  submitForReview(id: string) {
    return this.request<Record<string, unknown>>(`/api/v1/submissions/${id}/submit`, { method: 'POST' });
  }

  approveSubmission(id: string, note?: string) {
    return this.request<Record<string, unknown>>(`/api/v1/submissions/${id}/approve`, { method: 'POST', body: JSON.stringify({ note }) });
  }

  rejectSubmission(id: string, note: string) {
    return this.request<Record<string, unknown>>(`/api/v1/submissions/${id}/reject`, { method: 'POST', body: JSON.stringify({ note }) });
  }

  recallSubmission(id: string, reason?: string) {
    return this.request<Record<string, unknown>>(`/api/v1/submissions/${id}/recall`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  // Versions
  getVersions(submissionId: string) {
    return this.request<Record<string, unknown>[]>(`/api/v1/submissions/${submissionId}/versions`);
  }

  getDiff(submissionId: string, v1: number, v2: number) {
    return this.request<Record<string, unknown>>(`/api/v1/submissions/${submissionId}/diff/${v1}/${v2}`);
  }

  // Review Queue
  getReviewQueue(params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return this.request<{ data: Record<string, unknown>[]; pagination: Record<string, unknown> }>(`/api/v1/review-queue${query}`);
  }

  getReviewQueueStats() {
    return this.request<Record<string, unknown>>('/api/v1/review-queue/stats');
  }

  getLateChanges() {
    return this.request<Record<string, unknown>[]>('/api/v1/review-queue/late-changes');
  }

  // Notifications
  getNotifications(params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return this.request<{ data: Record<string, unknown>[]; pagination: Record<string, unknown>; unreadCount: number }>(`/api/v1/notifications${query}`);
  }

  markNotificationRead(id: string) {
    return this.request<Record<string, unknown>>(`/api/v1/notifications/${id}/read`, { method: 'PATCH' });
  }

  markAllNotificationsRead() {
    return this.request<Record<string, unknown>>('/api/v1/notifications/read-all', { method: 'POST' });
  }

  // Routing Rules
  getRoutingRules() {
    return this.request<Record<string, unknown>[]>('/api/v1/routing-rules');
  }

  updateRoutingRules(rules: Record<string, unknown>[]) {
    return this.request<Record<string, unknown>[]>('/api/v1/routing-rules', { method: 'PUT', body: JSON.stringify(rules) });
  }

  // Church Members (admin/pastor)
  getMembers(params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return this.request<{ data: Record<string, unknown>[]; pagination: Record<string, unknown> }>(`/api/v1/church/members${query}`);
  }

  createMember(data: { email: string; name: string; password: string; roles: string[] }) {
    return this.request<Record<string, unknown>>('/api/v1/church/members', { method: 'POST', body: JSON.stringify(data) });
  }

  updateMemberRoles(userId: string, roles: string[]) {
    return this.request<Record<string, unknown>>(`/api/v1/church/members/${userId}/roles`, { method: 'PATCH', body: JSON.stringify({ roles }) });
  }

  removeMember(userId: string) {
    return this.request<Record<string, unknown>>(`/api/v1/church/members/${userId}`, { method: 'DELETE' });
  }

  // Church Settings (admin/pastor)
  getChurchSettings() {
    return this.request<Record<string, unknown>>('/api/v1/church/settings');
  }

  updateChurchSettings(data: Record<string, unknown>) {
    return this.request<Record<string, unknown>>('/api/v1/church/settings', { method: 'PATCH', body: JSON.stringify(data) });
  }

  // Self-service Profile
  updateProfile(data: { name?: string; email?: string; phone?: string | null }) {
    return this.request<Record<string, unknown>>('/api/v1/auth/profile', { method: 'PATCH', body: JSON.stringify(data) });
  }

  changePassword(data: { currentPassword: string; newPassword: string }) {
    return this.request<Record<string, unknown>>('/api/v1/auth/change-password', { method: 'POST', body: JSON.stringify(data) });
  }

  // Calendar
  getCalendar(from: string, to: string) {
    return this.request<CalendarEvent[]>(`/api/v1/calendar?from=${from}&to=${to}`);
  }

  // Element Types
  getElementTypes() {
    return this.request<ElementType[]>('/api/v1/element-types');
  }

  createElementType(data: Record<string, unknown>) {
    return this.request<ElementType>('/api/v1/element-types', { method: 'POST', body: JSON.stringify(data) });
  }

  updateElementType(id: string, data: Record<string, unknown>) {
    return this.request<ElementType>(`/api/v1/element-types/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteElementType(id: string) {
    return this.request<{ success: boolean }>(`/api/v1/element-types/${id}`, { method: 'DELETE' });
  }

  // Service Order Items
  getServiceItems(serviceId: string) {
    return this.request<OrderItemDetail[]>(`/api/v1/services/${serviceId}/items`);
  }

  addServiceItem(serviceId: string, data: Record<string, unknown>) {
    return this.request<OrderItemDetail>(`/api/v1/services/${serviceId}/items`, { method: 'POST', body: JSON.stringify(data) });
  }

  reorderServiceItems(serviceId: string, items: { id: string; position: number }[]) {
    return this.request<{ success: boolean }>(`/api/v1/services/${serviceId}/items/reorder`, { method: 'PATCH', body: JSON.stringify({ items }) });
  }

  updateServiceItem(serviceId: string, itemId: string, data: Record<string, unknown>) {
    return this.request<OrderItemDetail>(`/api/v1/services/${serviceId}/items/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteServiceItem(serviceId: string, itemId: string) {
    return this.request<{ success: boolean }>(`/api/v1/services/${serviceId}/items/${itemId}`, { method: 'DELETE' });
  }

  searchServiceMembers(serviceId: string, search?: string) {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request<{ id: string; name: string; email: string }[]>(`/api/v1/services/${serviceId}/members${query}`);
  }

  // Publish / Unpublish
  publishService(id: string) {
    return this.request<ServiceDetail>(`/api/v1/services/${id}/publish`, { method: 'POST' });
  }

  unpublishService(id: string) {
    return this.request<ServiceDetail>(`/api/v1/services/${id}/unpublish`, { method: 'POST' });
  }

  // Songs
  getSongs(params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return this.request<{ data: SongDetail[]; pagination: Record<string, unknown> }>(`/api/v1/songs${query}`);
  }

  getSong(id: string) {
    return this.request<SongDetail>(`/api/v1/songs/${id}`);
  }

  createSong(data: Record<string, unknown>) {
    return this.request<SongDetail>('/api/v1/songs', { method: 'POST', body: JSON.stringify(data) });
  }

  updateSong(id: string, data: Record<string, unknown>) {
    return this.request<SongDetail>(`/api/v1/songs/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteSong(id: string) {
    return this.request<{ success: boolean }>(`/api/v1/songs/${id}`, { method: 'DELETE' });
  }

  createArrangement(songId: string, data: Record<string, unknown>) {
    return this.request<{ id: string; name: string; key: string }>(`/api/v1/songs/${songId}/arrangements`, { method: 'POST', body: JSON.stringify(data) });
  }

  async uploadSongResource(songId: string, file: File) {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${this.baseUrl}/api/v1/songs/${songId}/resources`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Error', message: 'Upload failed' }));
      throw error;
    }

    return response.json();
  }

  // Music Groups
  getMusicGroups() {
    return this.request<MusicGroupDetail[]>('/api/v1/music-groups');
  }

  getMusicGroup(id: string) {
    return this.request<MusicGroupDetail>(`/api/v1/music-groups/${id}`);
  }

  createMusicGroup(data: Record<string, unknown>) {
    return this.request<MusicGroupDetail>('/api/v1/music-groups', { method: 'POST', body: JSON.stringify(data) });
  }

  updateMusicGroup(id: string, data: Record<string, unknown>) {
    return this.request<MusicGroupDetail>(`/api/v1/music-groups/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteMusicGroup(id: string) {
    return this.request<{ success: boolean }>(`/api/v1/music-groups/${id}`, { method: 'DELETE' });
  }

  addGroupMember(groupId: string, data: Record<string, unknown>) {
    return this.request<Record<string, unknown>>(`/api/v1/music-groups/${groupId}/members`, { method: 'POST', body: JSON.stringify(data) });
  }

  removeGroupMember(groupId: string, memberId: string) {
    return this.request<{ success: boolean }>(`/api/v1/music-groups/${groupId}/members/${memberId}`, { method: 'DELETE' });
  }

  // Song Assignments
  getServiceSongs(serviceId: string) {
    return this.request<ServiceSongAssignment[]>(`/api/v1/services/${serviceId}/songs`);
  }

  assignSongToService(serviceId: string, data: Record<string, unknown>) {
    return this.request<Record<string, unknown>>(`/api/v1/services/${serviceId}/songs`, { method: 'POST', body: JSON.stringify(data) });
  }

  getMyAssignments(params?: Record<string, string>) {
    const query = params ? `?${new URLSearchParams(params)}` : '';
    return this.request<MyAssignment[]>(`/api/v1/me/song-assignments${query}`);
  }

  updateLearningStatus(assignmentId: string, status: string) {
    return this.request<Record<string, unknown>>(`/api/v1/musician-song-status/${assignmentId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
  }

  // Comments
  getComments(entityType: string, entityId: string) {
    return this.request<CommentWithThread[]>(`/api/v1/comments?entityType=${entityType}&entityId=${entityId}`);
  }

  createComment(data: Record<string, unknown>) {
    return this.request<Comment>('/api/v1/comments', { method: 'POST', body: JSON.stringify(data) });
  }

  updateComment(id: string, data: Record<string, unknown>) {
    return this.request<Comment>(`/api/v1/comments/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteComment(id: string) {
    return this.request<{ success: boolean }>(`/api/v1/comments/${id}`, { method: 'DELETE' });
  }

  resolveComment(id: string) {
    return this.request<Comment>(`/api/v1/comments/${id}/resolve`, { method: 'POST' });
  }

  unresolveComment(id: string) {
    return this.request<Comment>(`/api/v1/comments/${id}/unresolve`, { method: 'POST' });
  }

  // Content templates (FR-5..15)
  listTemplates(scope: 'personal' | 'standard', params?: { contentType?: string; page?: number; limit?: number }) {
    const search = new URLSearchParams({ scope });
    if (params?.contentType) search.set('contentType', params.contentType);
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    return this.request<Paginated<Template>>(`/api/v1/templates?${search.toString()}`);
  }

  getTemplate(id: string) {
    return this.request<Template>(`/api/v1/templates/${id}`);
  }

  createTemplate(data: CreateTemplateInput) {
    return this.request<Template>('/api/v1/templates', { method: 'POST', body: JSON.stringify(data) });
  }

  updateTemplate(id: string, data: UpdateTemplateInput) {
    return this.request<Template>(`/api/v1/templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteTemplate(id: string) {
    return this.request<{ success: true }>(`/api/v1/templates/${id}`, { method: 'DELETE' });
  }

  promoteSubmission(data: PromoteSubmissionInput) {
    return this.request<Template>('/api/v1/templates/promote', { method: 'POST', body: JSON.stringify(data) });
  }

  // Orphan triage (pastor/admin only)
  listOrphans() {
    return this.request<OrphanTemplate[]>('/api/v1/templates/orphans');
  }

  claimOrphan(id: string) {
    return this.request<Template>(`/api/v1/templates/orphans/${id}/claim`, { method: 'POST' });
  }

  promoteOrphan(id: string, data: PromoteOrphanInput) {
    return this.request<Template>(`/api/v1/templates/orphans/${id}/promote`, { method: 'POST', body: JSON.stringify(data) });
  }

  discardOrphan(id: string) {
    return this.request<{ success: true }>(`/api/v1/templates/orphans/${id}`, { method: 'DELETE' });
  }

  // Past-event archive (FR-3)
  listArchive(params?: { contentType?: string; page?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.contentType) search.set('contentType', params.contentType);
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<Paginated<Record<string, unknown>>>(`/api/v1/submissions/archive${qs ? `?${qs}` : ''}`);
  }

  // ===== Auxiliaries (SPEC_REPORT_GENERATION §2.1) =====
  listAuxiliaries() {
    return this.request<Auxiliary[]>('/api/v1/auxiliaries');
  }

  getAuxiliary(id: string) {
    return this.request<Auxiliary>(`/api/v1/auxiliaries/${id}`);
  }

  createAuxiliary(data: CreateAuxiliaryInput) {
    return this.request<Auxiliary>('/api/v1/auxiliaries', { method: 'POST', body: JSON.stringify(data) });
  }

  updateAuxiliary(id: string, data: UpdateAuxiliaryInput) {
    return this.request<Auxiliary>(`/api/v1/auxiliaries/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteAuxiliary(id: string) {
    return this.request<{ success: true }>(`/api/v1/auxiliaries/${id}`, { method: 'DELETE' });
  }

  listAuxiliaryMembers(auxiliaryId: string) {
    return this.request<AuxiliaryMember[]>(`/api/v1/auxiliaries/${auxiliaryId}/members`);
  }

  addAuxiliaryMember(auxiliaryId: string, data: AddAuxiliaryMemberInput) {
    return this.request<AuxiliaryMember>(`/api/v1/auxiliaries/${auxiliaryId}/members`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  setAuxiliaryMemberLeader(auxiliaryId: string, userId: string, data: SetAuxiliaryLeaderInput) {
    return this.request<AuxiliaryMember>(`/api/v1/auxiliaries/${auxiliaryId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  removeAuxiliaryMember(auxiliaryId: string, userId: string) {
    return this.request<{ success: true }>(`/api/v1/auxiliaries/${auxiliaryId}/members/${userId}`, { method: 'DELETE' });
  }

  // ===== Finance: funds (SPEC_REPORT_GENERATION §2.2) =====
  listFunds() {
    return this.request<Fund[]>('/api/v1/finance/funds');
  }

  createFund(data: CreateFundInput) {
    return this.request<Fund>('/api/v1/finance/funds', { method: 'POST', body: JSON.stringify(data) });
  }

  updateFund(id: string, data: Partial<CreateFundInput>) {
    return this.request<Fund>(`/api/v1/finance/funds/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteFund(id: string) {
    return this.request<{ success: true }>(`/api/v1/finance/funds/${id}`, { method: 'DELETE' });
  }

  // ===== Finance: budget lines =====
  listBudgetLines(params?: { fundId?: string; periodStart?: string; periodEnd?: string }) {
    const search = new URLSearchParams();
    if (params?.fundId) search.set('fundId', params.fundId);
    if (params?.periodStart) search.set('periodStart', params.periodStart);
    if (params?.periodEnd) search.set('periodEnd', params.periodEnd);
    const qs = search.toString();
    return this.request<BudgetLine[]>(`/api/v1/finance/budget-lines${qs ? `?${qs}` : ''}`);
  }

  createBudgetLine(data: CreateBudgetLineInput) {
    return this.request<BudgetLine>('/api/v1/finance/budget-lines', { method: 'POST', body: JSON.stringify(data) });
  }

  updateBudgetLine(id: string, data: Partial<CreateBudgetLineInput>) {
    return this.request<BudgetLine>(`/api/v1/finance/budget-lines/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteBudgetLine(id: string) {
    return this.request<{ success: true }>(`/api/v1/finance/budget-lines/${id}`, { method: 'DELETE' });
  }

  // ===== Finance: transactions =====
  listTransactions(params?: {
    fundId?: string;
    direction?: TransactionDirection;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.fundId) search.set('fundId', params.fundId);
    if (params?.direction) search.set('direction', params.direction);
    if (params?.from) search.set('from', params.from);
    if (params?.to) search.set('to', params.to);
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<Paginated<Transaction>>(`/api/v1/finance/transactions${qs ? `?${qs}` : ''}`);
  }

  createTransaction(data: CreateTransactionInput) {
    return this.request<Transaction>('/api/v1/finance/transactions', { method: 'POST', body: JSON.stringify(data) });
  }

  updateTransaction(id: string, data: Partial<CreateTransactionInput>) {
    return this.request<Transaction>(`/api/v1/finance/transactions/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  deleteTransaction(id: string) {
    return this.request<{ success: true }>(`/api/v1/finance/transactions/${id}`, { method: 'DELETE' });
  }

  // ===== Report templates (SPEC_REPORT_GENERATION §2.3) =====
  listReportTemplates(params?: {
    scope?: 'personal' | 'standard';
    format?: ReportFormat;
    page?: number;
    limit?: number;
  }) {
    const search = new URLSearchParams();
    if (params?.scope) search.set('scope', params.scope);
    if (params?.format) search.set('format', params.format);
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<Paginated<ReportTemplate>>(`/api/v1/reports/templates${qs ? `?${qs}` : ''}`);
  }

  getReportTemplate(id: string) {
    return this.request<ReportTemplate>(`/api/v1/reports/templates/${id}`);
  }

  createReportTemplate(data: ReportDefinition) {
    return this.request<ReportTemplate>('/api/v1/reports/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateReportTemplate(id: string, data: UpdateReportTemplateBody) {
    return this.request<ReportTemplate>(`/api/v1/reports/templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  deleteReportTemplate(id: string) {
    return this.request<{ success: true }>(`/api/v1/reports/templates/${id}`, { method: 'DELETE' });
  }

  promoteReportRun(data: PromoteRunInput) {
    return this.request<ReportTemplate>('/api/v1/reports/templates/promote-run', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ===== Report template orphans (pastor/admin) =====
  listReportOrphans() {
    return this.request<OrphanReportTemplate[]>('/api/v1/reports/templates/orphans');
  }

  claimReportOrphan(id: string) {
    return this.request<ReportTemplate>(`/api/v1/reports/templates/orphans/${id}/claim`, { method: 'POST' });
  }

  promoteReportOrphan(id: string, data: PromoteReportOrphanInput) {
    return this.request<ReportTemplate>(`/api/v1/reports/templates/orphans/${id}/promote`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  discardReportOrphan(id: string) {
    return this.request<{ success: true }>(`/api/v1/reports/templates/orphans/${id}`, { method: 'DELETE' });
  }

  // ===== Report runs (SPEC_REPORT_GENERATION §2.4) =====
  listReportRuns(params?: { status?: ReportRunStatus; page?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.status) search.set('status', params.status);
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<Paginated<ReportRun>>(`/api/v1/reports/runs${qs ? `?${qs}` : ''}`);
  }

  getReportRun(id: string) {
    return this.request<ReportRun>(`/api/v1/reports/runs/${id}`);
  }

  createReportRun(data: CreateReportRunBody) {
    return this.request<{ runId: string; status: 'queued' }>('/api/v1/reports/runs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  shareReportRun(id: string, data: ShareReportRunInput = {}) {
    return this.request<{ sharedCount: number; auxiliaryId: string }>(`/api/v1/reports/runs/${id}/share`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ===== Report inbox (SPEC_REPORT_GENERATION §2.5) =====
  listReportInbox(params?: { page?: number; limit?: number }) {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.limit !== undefined) search.set('limit', String(params.limit));
    const qs = search.toString();
    return this.request<Paginated<InboxItem>>(`/api/v1/reports/inbox${qs ? `?${qs}` : ''}`);
  }

  markReportShareRead(shareId: string) {
    return this.request<InboxItem>(`/api/v1/reports/inbox/${shareId}/read`, { method: 'POST' });
  }

  // Token management
  static setToken = setToken;
  static clearToken = clearToken;
  static getToken = getToken;
}

export const api = new ApiClient(API_URL);
